/**
 * Typed fetch wrapper for the admin SPA's cross-origin calls to the
 * Sill API's `/internal/admin/*` surface (ADR-0140).
 *
 * Modeled on `apps/dashboard/src/api/http-client.ts` — same shape,
 * trimmed to the surfaces the admin actually uses (JSON request +
 * response; no blob downloads, no text-body uploads).
 *
 * Cookie auth: `credentials: 'include'` ships the `sill_staff_session`
 * cookie cross-origin (admin runs on `console.sill.so`, API on
 * `api.sill.so`; cookie is `Domain=.sill.so`). Cloudflare Access
 * gates the hostname BEFORE this layer; this client doesn't see
 * unauthenticated traffic in production.
 *
 * On 401 we surface a typed error AND fire the `on_unauthorized`
 * callback so the AuthProvider can route to the login view —
 * identical contract to the dashboard's wrapper.
 */

export type FetchLike = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

export interface HttpClientDeps {
  readonly api_origin: string;
  readonly fetch_impl?: FetchLike;
  /**
   * Fired on 401 from any request. The AuthProvider wires this to
   * "clear the cached staff principal + route to the login view".
   */
  readonly on_unauthorized?: () => void;
}

export interface HttpRequestOptions {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly body?: unknown;
  /**
   * Query-string params; values are coerced to strings, `undefined`
   * entries are skipped. Saves callers from hand-rolling URLSearchParams.
   */
  readonly query?: Readonly<Record<string, string | number | undefined>>;
  /**
   * If true, the client maps 401 to {@link ApiError}('unauthorized')
   * WITHOUT invoking `on_unauthorized`. The bootstrap auth check sets
   * this so it can branch on 401 inline (route to login) rather than
   * triggering the provider-level callback before the provider has
   * mounted. Default false — in-flight calls from mounted views go
   * through the callback AND throw.
   */
  readonly throw_on_unauthorized?: boolean;
}

/**
 * Failure-mode taxonomy the admin SPA reasons about. Keyed off the
 * HTTP status code or a client-side condition.
 */
export type ApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'bad_request'
  | 'rate_limited'
  | 'service_unavailable'
  | 'server_error'
  | 'network_error'
  | 'bad_response';

export class ApiError extends Error {
  constructor(
    public readonly kind: ApiErrorKind,
    public readonly status: number | null,
    /**
     * Server-supplied `reason` field on 4xx/5xx responses, parsed off
     * the response body. Surfaces in the admin's error banners so
     * staff see e.g. `invalid_status` instead of opaque `bad_request:400`.
     */
    public readonly server_reason?: string,
    /** Parsed JSON error body (4xx/5xx), for routes that return details. */
    public readonly body?: unknown,
  ) {
    super(
      server_reason !== undefined && server_reason.length > 0
        ? `${kind}:${status ?? '?'} — ${server_reason}`
        : `${kind}${status !== null ? `:${status}` : ''}`,
    );
    this.name = 'ApiError';
  }
}

export interface HttpClient {
  request<T = unknown>(opts: HttpRequestOptions): Promise<T | undefined>;
}

function statusToKind(status: number): ApiErrorKind {
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status === 503) return 'service_unavailable';
  return 'server_error';
}

function buildQueryString(
  query: Readonly<Record<string, string | number | undefined>> | undefined,
): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s.length === 0 ? '' : `?${s}`;
}

export function createHttpClient(deps: HttpClientDeps): HttpClient {
  const fetch_fn = deps.fetch_impl ?? globalThis.fetch.bind(globalThis);

  return {
    async request<T = unknown>(opts: HttpRequestOptions): Promise<T | undefined> {
      const url = `${deps.api_origin}${opts.path}${buildQueryString(opts.query)}`;
      const headers: Record<string, string> =
        opts.body !== undefined ? { 'content-type': 'application/json' } : {};
      const init: RequestInit = {
        method: opts.method,
        // Sends the sill_staff_session cookie cross-origin. The API's
        // CORS config must echo Access-Control-Allow-Credentials:true
        // for the admin's origin; that's a server-side concern, not
        // this client's.
        credentials: 'include',
        headers,
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      };

      let res: Response;
      try {
        res = await fetch_fn(url, init);
      } catch {
        // Network reject, CORS-deny dropped by browser, abort.
        throw new ApiError('network_error', null);
      }

      // 401 — uniform unauthorized handling.
      if (res.status === 401) {
        if (opts.throw_on_unauthorized !== true) {
          deps.on_unauthorized?.();
        }
        throw new ApiError('unauthorized', 401);
      }

      // 204 — no body.
      if (res.status === 204) return undefined;

      // 200 / 201 / 202 — success branch, JSON-parse below.
      if (res.status === 200 || res.status === 201 || res.status === 202) {
        try {
          return (await res.json()) as T;
        } catch {
          throw new ApiError('bad_response', null);
        }
      }

      // Everything else — read the body's `reason` field if present
      // so the admin can surface the specific server-side cause.
      let server_reason: string | undefined;
      let error_body: unknown;
      try {
        const body = (await res.json()) as { reason?: unknown };
        error_body = body;
        if (typeof body.reason === 'string' && body.reason.length > 0) {
          server_reason = body.reason;
        }
      } catch {
        /* non-JSON / empty body — proceed without a reason */
      }
      throw new ApiError(statusToKind(res.status), res.status, server_reason, error_body);
    },
  };
}
